import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ConferenceEligibilityConfigurationModel,
  ConferenceApplicationModel,
  ConferenceApplicationReviewModel,
  ConferenceApplicationSubmitModel,
  ConferenceApplicationsResponseModel,
  ConferenceEligibilityRoleCategoryModel,
  ConferenceEligibilityRoleCategoryUserModel,
  ConferenceEligibilityEmployeeViewModel,
  ConferenceEligibilitySnapshotModel,
  ConferenceOpportunityModel
} from 'src/app/models/conferenceEligibilityModels';

@Injectable({
  providedIn: 'root'
})
export class ConferenceEligibilityService {
  private readonly baseUrl = `${environment.apiServer.webApiURL}/ConferenceEligibility`;

  constructor(private http: HttpClient) {
  }

  getConfiguration(): Observable<ConferenceEligibilityConfigurationModel> {
    return this.http.get<ConferenceEligibilityConfigurationModel>(`${this.baseUrl}/Configuration`);
  }

  saveConfiguration(configuration: ConferenceEligibilityConfigurationModel): Observable<ConferenceEligibilityConfigurationModel> {
    return this.http.post<ConferenceEligibilityConfigurationModel>(`${this.baseUrl}/Configuration`, configuration);
  }

  saveRoleCategory(roleCategory: ConferenceEligibilityRoleCategoryModel): Observable<ConferenceEligibilityConfigurationModel> {
    return this.http.post<ConferenceEligibilityConfigurationModel>(`${this.baseUrl}/RoleCategory`, roleCategory);
  }

  saveRoleCategoryUserOverride(
    override: ConferenceEligibilityRoleCategoryUserModel
  ): Observable<ConferenceEligibilityConfigurationModel> {
    return this.http.post<ConferenceEligibilityConfigurationModel>(`${this.baseUrl}/RoleCategoryUserOverride`, override);
  }

  deleteRoleCategoryUserOverride(overrideId: number): Observable<ConferenceEligibilityConfigurationModel> {
    return this.http.delete<ConferenceEligibilityConfigurationModel>(`${this.baseUrl}/RoleCategoryUserOverride/${overrideId}`);
  }

  getOpportunities(): Observable<ConferenceOpportunityModel[]> {
    return this.http.get<ConferenceOpportunityModel[]>(`${this.baseUrl}/Opportunities`);
  }

  saveOpportunity(opportunity: ConferenceOpportunityModel): Observable<ConferenceOpportunityModel> {
    return this.http.post<ConferenceOpportunityModel>(`${this.baseUrl}/Opportunity`, opportunity);
  }

  archiveOpportunity(opportunityId: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/Opportunity/${opportunityId}`);
  }

  getEligibility(userId: number): Observable<ConferenceEligibilitySnapshotModel> {
    return this.http.get<ConferenceEligibilitySnapshotModel>(`${this.baseUrl}/Eligibility/${userId}`);
  }

  getEmployeeView(userId?: number): Observable<ConferenceEligibilityEmployeeViewModel> {
    const suffix = userId ? `/${userId}` : '';
    return this.http.get<ConferenceEligibilityEmployeeViewModel>(`${this.baseUrl}/EmployeeView${suffix}`);
  }

  submitApplication(application: ConferenceApplicationSubmitModel): Observable<ConferenceApplicationModel> {
    return this.http.post<ConferenceApplicationModel>(`${this.baseUrl}/Application`, application);
  }

  getApplications(): Observable<ConferenceApplicationsResponseModel> {
    return this.http.get<ConferenceApplicationsResponseModel>(`${this.baseUrl}/Applications`);
  }

  getApplication(applicationId: number): Observable<ConferenceApplicationModel> {
    return this.http.get<ConferenceApplicationModel>(`${this.baseUrl}/Application/${applicationId}`);
  }

  reviewApplication(applicationId: number, review: ConferenceApplicationReviewModel): Observable<ConferenceApplicationModel> {
    return this.http.post<ConferenceApplicationModel>(`${this.baseUrl}/Application/${applicationId}/Review`, review);
  }

  cancelApplication(applicationId: number): Observable<ConferenceApplicationModel> {
    return this.http.post<ConferenceApplicationModel>(`${this.baseUrl}/Application/${applicationId}/Cancel`, {});
  }

  getAnswerFileUrl(answerId: number) {
    return `${this.baseUrl}/ApplicationAnswer/${answerId}/File`;
  }
}
